import React, { useEffect, useMemo, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Loader2, Search, Library, Plus, ChevronRight } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { hasOwnerAccess } from '../../lib/owner'
import { supabase } from '../../lib/supabaseClient'

// ReadingCatalogPage — /reading/catalog
//
// Two display modes:
//   1. Browse (search input empty) — category strips, one per
//      curated genre, each fetching its own slice in parallel.
//      Modelled on App Store / Apple Books: horizontal-scroll rows
//      with a "see all" affordance per section.
//   2. Search (input non-empty + submit) — flat card grid, the
//      original PR A behaviour. Pagination via "load more".
//
// Sources:
//   "عربي (أرشيف)" → Internet Archive Arabic texts (proxied)
//   "English (Gutenberg)" → Project Gutenberg via Gutendex
//
// Categories are pure frontend — each just maps to a query/topic
// the existing edge function understands. The function silently
// strips bare-Quran-page scans from the archive source so the
// default browse looks like real books, not scanned chapter pages.

// ----------------------------------------------------------------
// Category definitions
// ----------------------------------------------------------------
// Each category fires one parallel edge-function call on browse
// mount. Keep the list to ~6 to bound concurrency + total wait.
// Belt-and-braces filter for noise that slips past the upstream
// query — purely-numeric titles ("002") and titleless rows that
// archive.org occasionally surfaces for scan-chapter items.
function isUsableTitle(item) {
  const title = (item?.title || '').trim()
  if (!title) return false
  if (/^[\d\s.\-_]+$/.test(title)) return false
  return true
}

const CATEGORIES = {
  archive: [
    { key: 'recommended', query: '' },
    { key: 'novels',      query: 'روايات' },
    { key: 'islamic',     query: 'إسلام' },
    { key: 'history',     query: 'تاريخ' },
    { key: 'philosophy',  query: 'فلسفة' },
    { key: 'poetry',      query: 'شعر' },
  ],
  gutenberg: [
    { key: 'recommended',      topic: '' },
    { key: 'romance',          topic: 'romance' },
    { key: 'adventure',        topic: 'adventure' },
    { key: 'mystery',          topic: 'mystery' },
    { key: 'sciencefiction',   topic: 'science fiction' },
    { key: 'philosophy',       topic: 'philosophy' },
  ],
}

export function ReadingCatalogPage() {
  const { t, i18n } = useTranslation()
  const locale = i18n.language === 'ar' ? 'ar' : 'en'
  const navigate = useNavigate()
  const { user, loading: authLoading } = useAuth()

  const [source, setSource] = useState(locale === 'ar' ? 'archive' : 'gutenberg')
  const [query, setQuery] = useState('')
  const [submittedQuery, setSubmittedQuery] = useState('')
  const [searchItems, setSearchItems] = useState([])
  const [page, setPage] = useState(1)
  const [nextPage, setNextPage] = useState(null)
  const [loadingSearch, setLoadingSearch] = useState(false)
  const [error, setError] = useState(null)
  const [selected, setSelected] = useState(null)
  const [adding, setAdding] = useState(false)

  const browseMode = !submittedQuery.trim()

  const runSearch = async ({ append = false, nextPageNum = 1 } = {}) => {
    if (!submittedQuery.trim() && !append) return
    setLoadingSearch(true)
    setError(null)
    const { data, error: invokeErr } = await supabase.functions.invoke('reading-catalog-search', {
      body: { source, query: submittedQuery.trim(), page: nextPageNum },
    })
    setLoadingSearch(false)
    if (invokeErr) {
      setError(invokeErr.message || 'search failed')
      if (!append) { setSearchItems([]); setNextPage(null) }
      return
    }
    if (data?.error) {
      setError(data.error)
      if (!append) { setSearchItems([]); setNextPage(null) }
      return
    }
    const fresh = (data?.items || []).filter(isUsableTitle)
    setSearchItems((prev) => append ? [...prev, ...fresh] : fresh)
    setNextPage(data?.next_page ?? null)
    setPage(nextPageNum)
  }

  useEffect(() => {
    // Re-fire search when source flips during an active search session.
    if (submittedQuery.trim()) {
      setSearchItems([])
      setNextPage(null)
      setPage(1)
      runSearch({ append: false, nextPageNum: 1 })
    }
    // eslint-disable-next-line
  }, [source])

  const onSubmitSearch = (e) => {
    e?.preventDefault?.()
    const q = query.trim()
    setSubmittedQuery(q)
    setSearchItems([])
    setNextPage(null)
    setPage(1)
    if (q) {
      // small async detour so submittedQuery state has time to settle
      setTimeout(() => runSearch({ append: false, nextPageNum: 1 }), 0)
    }
  }

  const onSeeAllCategory = (cat) => {
    // "See all" on a strip switches the page into search mode using
    // the category's query as the term.
    const term = cat.query || cat.topic || ''
    setQuery(term)
    setSubmittedQuery(term)
    setSearchItems([])
    setNextPage(null)
    setPage(1)
    setTimeout(() => runSearch({ append: false, nextPageNum: 1 }), 0)
  }

  const clearSearch = () => {
    setQuery('')
    setSubmittedQuery('')
    setSearchItems([])
    setNextPage(null)
    setError(null)
  }

  const addToShelf = async (item) => {
    setAdding(true)
    setError(null)
    const { data, error: invokeErr } = await supabase.functions.invoke('reading-book-ingest', {
      body: {
        source: item.source,
        external_id: item.id,
        title: item.title,
        author: item.author,
        language: item.language,
        cover_url: item.cover_url,
        text_url: item.text_url,
      },
    })
    setAdding(false)
    if (invokeErr || data?.error) {
      // Prefer the human-friendly message field when the function
      // sent one (e.g. the OCR-garbled rejection); fall back to the
      // error code or SDK message otherwise.
      setError((invokeErr?.message) || data?.message || data?.error || 'ingest failed')
      return
    }
    if (data?.bookId) {
      setSelected(null)
      navigate(`/reading/${data.bookId}`, { replace: true })
    }
  }

  if (!authLoading && user && !hasOwnerAccess(user)) {
    return <Navigate to="/" replace />
  }
  if (authLoading) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin opacity-60" />
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-md space-y-4 p-4">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => navigate('/reading')}
          className="grid h-9 w-9 place-items-center rounded-full hover:bg-neutral-100 dark:hover:bg-neutral-800"
          aria-label={t('reading.back')}
        >
          <ArrowLeft className="h-5 w-5 rtl:-scale-x-100" />
        </button>
        <h1 dir="auto" className="text-lg font-semibold">{t('reading.catalogTitle')}</h1>
      </div>

      <div className="flex gap-1.5">
        <button
          type="button"
          onClick={() => setSource('archive')}
          className={`flex-1 rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
            source === 'archive' ? 'bg-rose-500 text-white' : 'bg-neutral-100 dark:bg-neutral-800'
          }`}
        >
          <span dir="auto">{t('reading.sources.archive')}</span>
        </button>
        <button
          type="button"
          onClick={() => setSource('gutenberg')}
          className={`flex-1 rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
            source === 'gutenberg' ? 'bg-rose-500 text-white' : 'bg-neutral-100 dark:bg-neutral-800'
          }`}
        >
          <span dir="auto">{t('reading.sources.gutenberg')}</span>
        </button>
      </div>

      <form onSubmit={onSubmitSearch} className="flex items-center gap-2 rounded-full border border-neutral-200 bg-neutral-50 px-3 py-1.5 dark:border-neutral-700 dark:bg-neutral-900">
        <Search className="h-4 w-4 opacity-60" />
        <input
          dir="auto"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('reading.searchPlaceholder')}
          className="flex-1 bg-transparent text-sm outline-none"
        />
        {submittedQuery && (
          <button
            type="button"
            onClick={clearSearch}
            className="rounded-full bg-neutral-200 px-2 py-0.5 text-[10px] font-medium dark:bg-neutral-700"
          >
            {t('reading.clearSearch')}
          </button>
        )}
        <button
          type="submit"
          disabled={loadingSearch}
          className="rounded-full bg-rose-500 px-3 py-1 text-xs font-medium text-white disabled:opacity-50"
        >
          <span dir="auto">{t('reading.searchGo')}</span>
        </button>
      </form>

      {error && (
        <p dir="auto" className="text-center text-xs text-rose-500">{error}</p>
      )}

      {browseMode ? (
        <div className="space-y-6">
          {CATEGORIES[source].map((cat) => (
            <CategoryStrip
              key={`${source}-${cat.key}`}
              source={source}
              category={cat}
              onSelect={setSelected}
              onSeeAll={() => onSeeAllCategory(cat)}
            />
          ))}
        </div>
      ) : (
        <SearchGrid
          items={searchItems}
          loading={loadingSearch}
          nextPage={nextPage}
          onSelect={setSelected}
          onLoadMore={() => runSearch({ append: true, nextPageNum: nextPage })}
        />
      )}

      {selected && (
        <BookDetailSheet
          item={selected}
          adding={adding}
          onClose={() => setSelected(null)}
          onAdd={() => addToShelf(selected)}
          t={t}
        />
      )}
    </div>
  )
}

// ----------------------------------------------------------------
// CategoryStrip — one horizontal row, self-fetching
// ----------------------------------------------------------------
function CategoryStrip({ source, category, onSelect, onSeeAll }) {
  const { t } = useTranslation()
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let alive = true
    setLoading(true)
    supabase.functions.invoke('reading-catalog-search', {
      body: {
        source,
        query: category.query || '',
        topic: category.topic || '',
        page: 1,
      },
    }).then(({ data }) => {
      if (!alive) return
      const filtered = (data?.items || []).filter(isUsableTitle)
      setItems(filtered.slice(0, 10))
      setLoading(false)
    }).catch(() => {
      if (alive) setLoading(false)
    })
    return () => { alive = false }
  }, [source, category.key, category.query, category.topic])

  return (
    <section>
      <button
        type="button"
        onClick={onSeeAll}
        className="mb-2 flex w-full items-center justify-between text-start"
      >
        <span dir="auto" className="text-sm font-semibold">
          {t(`reading.categories.${category.key}`)}
        </span>
        <span className="flex items-center gap-0.5 text-[11px] opacity-60">
          <span dir="auto">{t('reading.seeAll')}</span>
          <ChevronRight className="h-3 w-3 rtl:-scale-x-100" />
        </span>
      </button>

      {loading ? (
        <div className="flex h-40 items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin opacity-60" />
        </div>
      ) : items.length === 0 ? (
        <p dir="auto" className="py-6 text-center text-xs opacity-50">{t('reading.catalogEmpty')}</p>
      ) : (
        <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1" dir="ltr">
          {items.map((item) => (
            <BookTile key={`${item.source}-${item.id}`} item={item} onSelect={onSelect} />
          ))}
        </div>
      )}
    </section>
  )
}

// ----------------------------------------------------------------
// SearchGrid — flat results for typed queries
// ----------------------------------------------------------------
function SearchGrid({ items, loading, nextPage, onSelect, onLoadMore }) {
  const { t } = useTranslation()

  if (loading && items.length === 0) {
    return (
      <div className="flex h-[40vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin opacity-60" />
      </div>
    )
  }
  if (items.length === 0) {
    return <p dir="auto" className="pt-8 text-center text-sm opacity-70">{t('reading.catalogEmpty')}</p>
  }
  return (
    <>
      <div className="grid grid-cols-2 gap-3">
        {items.map((item) => (
          <BookCard key={`${item.source}-${item.id}`} item={item} onSelect={onSelect} />
        ))}
      </div>
      {nextPage && (
        <button
          type="button"
          onClick={onLoadMore}
          disabled={loading}
          className="mx-auto mt-3 flex items-center gap-2 rounded-full bg-neutral-100 px-4 py-1.5 text-xs font-medium dark:bg-neutral-800"
        >
          {loading && <Loader2 className="h-3 w-3 animate-spin" />}
          <span dir="auto">{t('reading.loadMore')}</span>
        </button>
      )}
    </>
  )
}

// ----------------------------------------------------------------
// BookTile — fixed-width card used inside horizontal strips
// ----------------------------------------------------------------
function BookTile({ item, onSelect }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(item)}
      className="w-28 flex-shrink-0 overflow-hidden rounded-xl border border-neutral-200 bg-card text-start dark:border-neutral-800"
    >
      <div className="aspect-[2/3] bg-neutral-200 dark:bg-neutral-800">
        {item.cover_url ? (
          <img
            src={item.cover_url}
            alt=""
            className="h-full w-full object-cover"
            onError={(e) => { e.currentTarget.style.display = 'none' }}
          />
        ) : (
          <div className="grid h-full place-items-center">
            <Library className="h-6 w-6 opacity-30" />
          </div>
        )}
      </div>
      <div className="space-y-0.5 p-1.5">
        <p dir="auto" className="line-clamp-2 text-[11px] font-medium leading-snug">{item.title}</p>
        {item.author && (
          <p dir="auto" className="line-clamp-1 text-[9px] opacity-60">{item.author}</p>
        )}
      </div>
    </button>
  )
}

// ----------------------------------------------------------------
// BookCard — two-column grid card for search results
// ----------------------------------------------------------------
function BookCard({ item, onSelect }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(item)}
      className="overflow-hidden rounded-2xl border border-neutral-200 bg-card text-start dark:border-neutral-800"
    >
      <div className="aspect-[2/3] bg-neutral-200 dark:bg-neutral-800">
        {item.cover_url ? (
          <img
            src={item.cover_url}
            alt=""
            className="h-full w-full object-cover"
            onError={(e) => { e.currentTarget.style.display = 'none' }}
          />
        ) : (
          <div className="grid h-full place-items-center">
            <Library className="h-8 w-8 opacity-30" />
          </div>
        )}
      </div>
      <div className="space-y-0.5 p-2">
        <p dir="auto" className="line-clamp-2 text-sm font-medium leading-snug">{item.title}</p>
        {item.author && (
          <p dir="auto" className="line-clamp-1 text-[11px] opacity-60">{item.author}</p>
        )}
      </div>
    </button>
  )
}

// ----------------------------------------------------------------
// BookDetailSheet — bottom sheet shown on tap
// ----------------------------------------------------------------
function BookDetailSheet({ item, adding, onClose, onAdd, t }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end bg-black/40" onClick={onClose}>
      <div
        className="w-full rounded-t-3xl bg-card p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-neutral-300 dark:bg-neutral-700" />
        <div className="flex gap-3">
          <div className="aspect-[2/3] w-24 flex-shrink-0 overflow-hidden rounded-xl bg-neutral-200 dark:bg-neutral-800">
            {item.cover_url ? (
              <img
                src={item.cover_url}
                alt=""
                className="h-full w-full object-cover"
                onError={(e) => { e.currentTarget.style.display = 'none' }}
              />
            ) : (
              <div className="grid h-full place-items-center">
                <Library className="h-8 w-8 opacity-30" />
              </div>
            )}
          </div>
          <div className="min-w-0 flex-1 space-y-1">
            <h2 dir="auto" className="text-base font-semibold leading-tight">{item.title}</h2>
            {item.author && <p dir="auto" className="text-sm opacity-70">{item.author}</p>}
            <p dir="auto" className="text-[10px] uppercase tracking-wide opacity-50">
              {t(`reading.sources.${item.source}`)} · {item.language}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onAdd}
          disabled={adding}
          className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-rose-500 px-5 py-2.5 text-sm font-medium text-white disabled:opacity-50"
        >
          {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          <span dir="auto">{t('reading.addToShelf')}</span>
        </button>
      </div>
    </div>
  )
}
